'use client'
import type { TFormField } from '@/components/project/ai-chat/editor/_types'
import { FormConfigUI } from '@/components/forms/form-config-ui'
import { AddFormCustomFieldDialog } from '@/components/modals/form-custom-field/add'
import { EditFormCustomFieldDialog } from '@/components/modals/form-custom-field/edit'
import { useConfirm } from '@/hooks/use-confirm'
import { useCurrentUser } from '@/hooks/use-current-user'
import { useProjectStore } from '@/hooks/use-project-store'
import {
  extractFormFieldsFromSchema,
  getFormConfiguration,
  removeFormField,
} from '@/lib/editor/form-config'
import { useTranslations } from 'next-intl'
import { useCallback, useEffect, useRef, useState } from 'react'

interface FormEditPanelWrapperProps {
  schemaId: string
  onEmailSettingsChange?: (changed: boolean) => void
  onFormConfigChange?: (config: {
    sendNotificationEmail: boolean
    sendFeedbackEmail: boolean
    emailSubject: string
    emailTemplate: string
    replyToEmail: string
  }) => void
  onFormFieldsChange?: (changed: boolean) => void
}

export function FormEditPanelWrapper({
  schemaId,
  onEmailSettingsChange,
  onFormConfigChange,
  onFormFieldsChange,
}: FormEditPanelWrapperProps) {
  const [showAddFormCustomFieldDialog, setShowAddFormCustomFieldDialog] =
    useState(false)
  const [showEditFormFieldDialog, setShowEditFormFieldDialog] = useState(false)
  const [editingField, setEditingField] = useState<TFormField | null>(null)
  const [formFields, setFormFields] = useState<TFormField[]>([])
  const [sendNotificationEmail, setSendNotificationEmail] = useState(false)
  const [sendFeedbackEmail, setSendFeedbackEmail] = useState(false)
  const [emailSubject, setEmailSubject] = useState(
    'Thank you for your submission',
  )
  const [emailTemplate, setEmailTemplate] = useState('')
  const [replyToEmail, setReplyToEmail] = useState('')
  const t = useTranslations('editor')
  const { data: user } = useCurrentUser()
  const { project, setProject } = useProjectStore()
  const { confirm } = useConfirm()

  // Track if we've done the initial load to avoid calling onFormConfigChange during mount
  const isInitialMount = useRef(true)

  // Load form configuration on mount
  useEffect(() => {
    const config = getFormConfiguration(project?.file_system_tree, schemaId)

    if (config) {
      // Load email settings
      setSendNotificationEmail(config.sendNotificationEmail || false)
      setSendFeedbackEmail(config.sendFeedbackEmail || false)
      setEmailSubject(config.emailSubject || 'Thank you for your submission')
      setEmailTemplate(config.emailTemplate || '')
      setReplyToEmail(config.replyToEmail || '')

      // Load form fields
      if (config.jsonSchema) {
        const schemaFields = extractFormFieldsFromSchema(config.jsonSchema)
        setFormFields(schemaFields)
      } else {
        setFormFields(config.customFields || [])
      }
    }
  }, [schemaId, project?.file_system_tree])

  // Notify parent when configuration changes (but not on initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }

    if (onFormConfigChange) {
      onFormConfigChange({
        sendNotificationEmail,
        sendFeedbackEmail,
        emailSubject,
        emailTemplate,
        replyToEmail,
      })
    }
  }, [
    sendNotificationEmail,
    sendFeedbackEmail,
    emailSubject,
    emailTemplate,
    replyToEmail,
  ])

  const handleEmailSettingChange = useCallback(() => {
    if (onEmailSettingsChange) {
      onEmailSettingsChange(true)
    }
  }, [onEmailSettingsChange])

  const handleEmailToggle = async (
    _settingName: 'notification' | 'feedback',
    currentValue: boolean,
    setValue: (value: boolean) => void,
  ) => {
    // If turning ON the email feature, show confirmation
    if (!currentValue) {
      const isConfirmed = await confirm({
        title: t('form.email_cost_warning_title'),
        description: t('form.email_cost_warning_description'),
        okText: t('form.enable_email'),
        cancelText: t('cancel'),
      })

      if (isConfirmed) {
        setValue(true)
        handleEmailSettingChange()
      }
    } else {
      // If turning OFF, no confirmation needed
      setValue(false)
      handleEmailSettingChange()
    }
  }

  const handleDeleteField = useCallback(
    async (field: TFormField) => {
      const isConfirmed = await confirm({
        title: t('form.delete_field'),
        description: t('form.delete_field_confirmation', {
          name: field.label,
        }),
        okText: t('form.delete'),
        cancelText: t('cancel'),
        okClassName: 'bg-red-500 hover:bg-red-600 text-white',
      })

      if (isConfirmed) {
        const updatedTree = removeFormField(
          project?.file_system_tree,
          schemaId,
          field.id,
        )
        setProject({
          ...project!,
          file_system_tree: updatedTree,
        })
        if (onFormFieldsChange) {
          onFormFieldsChange(true)
        }
        const config = getFormConfiguration(updatedTree, schemaId)
        if (config?.jsonSchema) {
          const schemaFields = extractFormFieldsFromSchema(config.jsonSchema)
          setFormFields(schemaFields)
        } else {
          setFormFields(config?.customFields || [])
        }
      }
    },
    [confirm, project, schemaId, setProject, onFormFieldsChange, t],
  )

  return (
    <>
      <FormConfigUI
        formFields={formFields}
        sendNotificationEmail={sendNotificationEmail}
        sendFeedbackEmail={sendFeedbackEmail}
        emailSubject={emailSubject}
        emailTemplate={emailTemplate}
        replyToEmail={replyToEmail}
        userEmail={user?.email}
        onAddFieldClick={() => setShowAddFormCustomFieldDialog(true)}
        onEditFieldClick={(field) => {
          setEditingField(field)
          setShowEditFormFieldDialog(true)
        }}
        onDeleteFieldClick={handleDeleteField}
        onNotificationToggle={() =>
          handleEmailToggle(
            'notification',
            sendNotificationEmail,
            setSendNotificationEmail,
          )
        }
        onFeedbackToggle={() =>
          handleEmailToggle('feedback', sendFeedbackEmail, setSendFeedbackEmail)
        }
        onEmailSubjectChange={(value) => {
          setEmailSubject(value)
          handleEmailSettingChange()
        }}
        onEmailTemplateChange={(value) => {
          setEmailTemplate(value)
          handleEmailSettingChange()
        }}
        onReplyToEmailChange={(value) => {
          setReplyToEmail(value)
          handleEmailSettingChange()
        }}
      />
      {/* Dialogs */}
      <AddFormCustomFieldDialog
        open={showAddFormCustomFieldDialog}
        onOpenChange={setShowAddFormCustomFieldDialog}
        schemaId={schemaId}
        onSuccess={(updatedTree) => {
          // Refresh form fields after adding a new field
          setShowAddFormCustomFieldDialog(false)
          // Notify parent that fields have changed
          if (onFormFieldsChange) {
            onFormFieldsChange(true)
          }
          // Reload fields from the updated tree
          const config = getFormConfiguration(
            updatedTree || project?.file_system_tree,
            schemaId,
          )
          if (config?.jsonSchema) {
            const schemaFields = extractFormFieldsFromSchema(config.jsonSchema)
            setFormFields(schemaFields)
          } else {
            setFormFields(config?.customFields || [])
          }
        }}
      />
      {editingField && (
        <EditFormCustomFieldDialog
          open={showEditFormFieldDialog}
          onOpenChange={setShowEditFormFieldDialog}
          field={editingField}
          schemaId={schemaId}
          onSuccess={(updatedTree) => {
            // Refresh form fields after editing
            setShowEditFormFieldDialog(false)
            setEditingField(null)
            // Notify parent that fields have changed
            if (onFormFieldsChange) {
              onFormFieldsChange(true)
            }
            // Reload fields from the updated tree
            const config = getFormConfiguration(
              updatedTree || project?.file_system_tree,
              schemaId,
            )
            if (config?.jsonSchema) {
              const schemaFields = extractFormFieldsFromSchema(
                config.jsonSchema,
              )
              setFormFields(schemaFields)
            } else {
              setFormFields(config?.customFields || [])
            }
          }}
        />
      )}
    </>
  )
}
